import { addDays, differenceInDays, startOfDay } from 'date-fns';

import { GoalThreshold } from '../../../coaching-base/config';
import {
    CoachingType,
    CommitmentKpi,
    FocusForPerformerFocusHistory,
    GoalStatus,
} from '../../../coaching-base/general';
import { CoachingSwitchboardService } from '../../../coaching-base/switchboard';
import { GoalsCollection } from '../../../coaching-commitment/goals-collection.model';
import { epicDependencies } from '../../../store';
import { getSubBehaviourById } from '../focus-selection/focus-selection.function';
import { FocusSelectorState } from '../focus-selector.state';
import {
    CellNode,
    Column,
    Row,
    RowNode,
    Sort,
    SortOrder,
} from '../grid/experimental.grid';
import { FocusSelectorBehaviourModel } from '../model/focus-selector-behaviour.model';
import { FocusSelectorDaysToAchievement } from '../model/focus-selector-days-to-achievement.model';
import { FocusSelectorFocusModel } from '../model/focus-selector-focus.model';
import { FocusSelectorGoalModel } from '../model/focus-selector-goal.model';
import {
    CellContext,
    ColumnID,
    defaultSortDefs,
    LoadGoalsGrid,
    RowContext,
} from '../model/focus-selector-load-goals.grid.model';
import { FocusSelectorMetricModel } from '../model/focus-selector-metric.model';
import {
    ProjectionEvaluationRangeId,
    ProjectionEvaluationRanges,
} from '../model/focus-selector-projection-evaluation-range.enum';
import { TemplateID } from '../model/focus-selector.grid.model';
import {
    LoadGoalsSelectedGoal,
    LoadGoalsSelectedGoals,
} from '../model/load-goals-selected.model';

export function makeColumns(): Column[] {
    return [
        {
            id: ColumnID.METRIC,
            ctx: { templateId: TemplateID.HEADER },
            setWidth: 200,
            minWidth: 200,
            compareFn: compare,
        },
        {
            id: ColumnID.OA,
            ctx: { templateId: TemplateID.HEADER },
            setWidth: 100,
            minWidth: 100,
            compareFn: compare,
        },
        {
            id: ColumnID.ON_SCORECARD,
            ctx: { templateId: TemplateID.HEADER },
            setWidth: 100,
            minWidth: 100,
            compareFn: compare,
        },
        {
            id: ColumnID.ACTUAL,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 100,
            compareFn: compare,
        },
        {
            id: ColumnID.TARGET,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 100,
            compareFn: compare,
        },
        {
            id: ColumnID.PROJECTED_15_DAY_IMPROVEMENT,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 150,
            compareFn: compare,
        },
        {
            id: ColumnID.PROJECTED_30_DAY_IMPROVEMENT,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 150,
            compareFn: compare,
        },
        {
            id: ColumnID.PROJECTED_60_DAY_IMPROVEMENT,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 150,
            compareFn: compare,
        },
        {
            id: ColumnID.IS_SELECTED_METRIC,
            ctx: { templateId: TemplateID.HEADER },
            compareFn: compare,
            hidden: true,
        },
    ];
}

export function compare(
    cellA: CellNode,
    cellB: CellNode,
    rowA: RowNode,
    rowB: RowNode,
    def: Sort,
): number {
    const goalA: FocusSelectorGoalModel = rowA.context.ctx.goal;
    const goalB: FocusSelectorGoalModel = rowB.context.ctx.goal;
    let valA: number;
    let valB: number;
    switch (def.columnId) {
        case ColumnID.IS_SELECTED_METRIC:
            valA = goalA.isSelectedMetric ? 999 : 1;
            valB = goalB.isSelectedMetric ? 999 : 1;
            break;
        case ColumnID.PROJECTED_30_DAY_IMPROVEMENT:
            valA = goalA.projected30 ?? Number.MIN_SAFE_INTEGER;
            valB = goalB.projected30 ?? Number.MIN_SAFE_INTEGER;
            break;
        default:
            return Number.MIN_SAFE_INTEGER;
    }

    return def.order === SortOrder.ASC ? valA - valB : valB - valA;
}

export function makeRows(
    goals: FocusSelectorGoalModel[],
): LoadGoalsGrid['rows'] {
    return goals.map<Row<RowContext, CellContext>>((goal) => {
        const {
            metricId,
            metric,
            oa,
            isInScorecard,
            actual,
            target,
            projected15,
            projected30,
            projected60,
            isSelectedMetric,
        } = goal;

        return {
            id: metricId.toString(),
            ctx: {
                templateId: TemplateID.CONTENT,
                goal,
            },
            cells: [
                {
                    columnId: ColumnID.METRIC,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        checked: isSelectedMetric,
                        metric,
                    },
                },
                {
                    columnId: ColumnID.OA,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        oa,
                    },
                },
                {
                    columnId: ColumnID.ON_SCORECARD,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        isInScorecard,
                    },
                },
                {
                    columnId: ColumnID.ACTUAL,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        actual,
                    },
                },
                {
                    columnId: ColumnID.TARGET,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        target,
                    },
                },
                {
                    columnId: ColumnID.PROJECTED_15_DAY_IMPROVEMENT,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        projected15,
                    },
                },
                {
                    columnId: ColumnID.PROJECTED_30_DAY_IMPROVEMENT,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        projected30,
                    },
                },
                {
                    columnId: ColumnID.PROJECTED_60_DAY_IMPROVEMENT,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        projected60,
                    },
                },
            ],
        };
    });
}

export function formatGoals(
    selectedMetricIds: number[],
    coachableMetrics: FocusSelectorMetricModel[],
    selectedFocus: FocusSelectorFocusModel,
    behaviours: FocusSelectorBehaviourModel[],
): FocusSelectorGoalModel[] | null {
    if (
        selectedMetricIds.length === 0 ||
        selectedFocus?.behaviour?.id == null ||
        selectedFocus?.subBehaviour?.id == null
    ) {
        return null;
    }

    const subBehaviour = getSubBehaviourById(
        selectedFocus.subBehaviour.id,
        selectedFocus.behaviour.id,
        behaviours,
    );

    return coachableMetrics.map((metric) => {
        const projectionResults =
            subBehaviour?.dataPerMetricId?.[metric?.id]
                ?.projectionResultPerRangeId;

        return {
            metricId: metric?.id ?? null,
            metric: metric?.name ?? '',
            oa: metric?.coachTypeId ?? null,
            isInScorecard: metric?.isInScorecard ?? false,
            actual: metric?.actual ?? 0,
            target: metric?.target ?? 0,
            projected15:
                projectionResults?.[ProjectionEvaluationRangeId.DAY_15]
                    ?.projectionResult ?? null,
            projected30:
                projectionResults?.[ProjectionEvaluationRangeId.DAY_30]
                    ?.projectionResult ?? null,
            projected60:
                projectionResults?.[ProjectionEvaluationRangeId.DAY_60]
                    ?.projectionResult ?? null,
            isSelectedMetric: selectedMetricIds.includes(metric.id),
            rankOrder: metric?.rankOrder ?? null,
        };
    });
}

export function selectGoals(
    goals: FocusSelectorGoalModel[],
    selectedMetricIds: Array<FocusSelectorMetricModel['id']>,
): FocusSelectorGoalModel[] {
    return goals.map((goal) => ({
        ...goal,
        isSelectedMetric: selectedMetricIds.includes(goal.metricId),
    }));
}

export function findGoal(
    metricId: FocusSelectorMetricModel['id'],
    goals: FocusSelectorGoalModel[],
): FocusSelectorGoalModel {
    return goals.find((goal) => goal.metricId === metricId);
}

export function calcDefaultExpectedAchievementDate(
    selectedProjEvalRangeId: ProjectionEvaluationRangeId,
): Date {
    const dayRange = ProjectionEvaluationRanges[selectedProjEvalRangeId];
    return addDays(new Date(), dayRange);
}

export function rankOrderToModifier(rankOrder: number): number {
    return rankOrder === 0 ? -1 : 1;
}

export function calcGapToTarget(
    target: number,
    actual: number,
    rankOrder: number,
): number {
    return ((target - actual) / target) * 100 * rankOrderToModifier(rankOrder);
}

export function getDefaultPercImprovementExpected(
    goal: FocusSelectorGoalModel,
    selectedProjEcvalRangeId: ProjectionEvaluationRangeId,
): number {
    if (selectedProjEcvalRangeId === ProjectionEvaluationRangeId.DAY_15) {
        return goal.projected15 * 100;
    }

    if (selectedProjEcvalRangeId === ProjectionEvaluationRangeId.DAY_30) {
        return goal.projected30 * 100;
    }

    if (selectedProjEcvalRangeId === ProjectionEvaluationRangeId.DAY_60) {
        return goal.projected60 * 100;
    }

    return null;
}

export function calcGoalValueFromPercImprovement(
    percImprovementExpected: number,
    actual: number,
    rankOrder: number,
): number {
    if (percImprovementExpected == null) {
        return null;
    }

    return (
        actual * (1 + percImprovementExpected / rankOrderToModifier(rankOrder))
    );
}

export function calcPercImprovementFromGoalValue(
    goalValue: number,
    actual: number,
    rankOrder: number,
): number {
    return (
        ((goalValue - actual) / actual) * rankOrderToModifier(rankOrder) * 100
    );
}

export function calcStartPercToGoal(
    goalValue: number,
    actual: number,
    rankOrder: number,
): number {
    if (goalValue == null) {
        return null;
    }

    const variance =
        ((actual - goalValue) / goalValue) * rankOrderToModifier(rankOrder);
    return (variance + 1) * 100;
}

export function dateIsValid(date: Date): boolean {
    return +startOfDay(date) >= +startOfDay(new Date());
}

export function sortGoalThresholds(
    goalThresholds: GoalThreshold[],
): GoalThreshold[] {
    return (goalThresholds != null ? [...goalThresholds] : []).sort(
        (a, b) => a.threshold - b.threshold,
    );
}

export function determineDaysToAchievement(
    estimatedCompletionDate: Date,
    goalThresholds: GoalThreshold[],
): FocusSelectorDaysToAchievement {
    const now = new Date();
    const daysToAchievement =
        differenceInDays(
            startOfDay(estimatedCompletionDate),
            startOfDay(now),
        ) ?? null;

    let goalThreshold: GoalThreshold;
    if (daysToAchievement != null) {
        goalThreshold = goalThresholds.find(
            (e) => daysToAchievement < e.threshold,
        );
    }

    return {
        daysToAchievement,
        daysToAchievementColor: goalThreshold?.color ?? '#000',
    };
}

export function createSelectedGoals(
    selectedMetricIds: number[],
    goals: FocusSelectorGoalModel[],
    goalsCollection: GoalsCollection,
    projectionEvaluationRange: ProjectionEvaluationRangeId,
    commitmentId?: number,
    updateFromCellChange: boolean = false,
): FocusSelectorState['selectedGoals'] {
    const selectedGoals: LoadGoalsSelectedGoals = {};

    const service = epicDependencies.injector.get<CoachingSwitchboardService>(
        CoachingSwitchboardService,
    );

    const activeCmtsRecords =
        service.pillarTwo.sbHasActiveCommitmentWithGoal(selectedMetricIds);
    for (const metricId of selectedMetricIds) {
        let currentGoals: CommitmentKpi[] = [];
        const hasDraftCmKpi = goalsCollection?.commitmentKpis.find(
            (e) =>
                e.metricId === metricId &&
                ((e.statusId === GoalStatus.ACTIVE &&
                    e.isOriginalGoal !== true) ||
                    e.goalId == null),
        );
        const hasActiveCmtKpi = goalsCollection?.commitmentKpis.find(
            (e) =>
                e.metricId === metricId &&
                e.statusId === GoalStatus.ACTIVE &&
                e.isOriginalGoal === true,
        );

        if (goalsCollection?.goalsFromOtherCommitments != null) {
            currentGoals = [...goalsCollection.goalsFromOtherCommitments];
        }

        const existingGoal = currentGoals.find(
            (commitmentKpi) => commitmentKpi.metricId === metricId,
        );

        const focusSelectorGoal = goals.find(
            (goal) => goal.metricId === metricId,
        );

        let activeGoal = null;
        const hasActiveCommitment = activeCmtsRecords[metricId];
        if (
            (existingGoal != null ||
                (focusSelectorGoal != null &&
                    focusSelectorGoal.oa !== CoachingType.UNCOACHED)) &&
            (hasActiveCmtKpi == null || hasActiveCommitment) &&
            commitmentId == null
        ) {
            activeGoal = existingGoal ?? focusSelectorGoal ?? {};
        }

        if (activeGoal != null) {
            selectedGoals[metricId] = {
                form: null,
                daysToAchievement: null,
                activeGoal,
            };
        } else {
            const calcValues = {
                actual: Number(
                    focusSelectorGoal?.actual ??
                        hasDraftCmKpi?.actual ??
                        hasActiveCmtKpi?.actual ??
                        existingGoal?.actual,
                ),
                target: Number(
                    focusSelectorGoal?.target ??
                        hasDraftCmKpi?.target ??
                        hasActiveCmtKpi?.target ??
                        existingGoal?.target,
                ),
                rankOrder: focusSelectorGoal?.rankOrder,
                expectedAchievementDate:
                    hasDraftCmKpi?.estimatedCompletionDate ??
                    hasActiveCmtKpi?.estimatedCompletionDate ??
                    existingGoal?.estimatedCompletionDate ??
                    null,
                goalValue: Number(
                    hasDraftCmKpi?.goalValue ??
                        hasActiveCmtKpi?.goalValue ??
                        existingGoal?.goalValue ??
                        null,
                ),
            };

            const currentActual = calcValues.actual;

            const expectedAchievementDate =
                calcValues?.expectedAchievementDate != null &&
                !updateFromCellChange
                    ? new Date(calcValues?.expectedAchievementDate)
                    : calcDefaultExpectedAchievementDate(
                          projectionEvaluationRange,
                      );

            const percentGapToTarget = calcGapToTarget(
                calcValues.target,
                calcValues.actual,
                calcValues.rankOrder,
            );

            let percentImprovementExpected = getDefaultPercImprovementExpected(
                focusSelectorGoal,
                projectionEvaluationRange,
            );
            let goalValue = calcGoalValueFromPercImprovement(
                percentImprovementExpected / 100,
                focusSelectorGoal.actual,
                focusSelectorGoal.rankOrder,
            );
            goalValue =
                calcValues.goalValue !== 0 ? calcValues.goalValue : goalValue;

            // explicit calculate for 30 days
            if (
                projectionEvaluationRange === 30 &&
                percentImprovementExpected === 0
            ) {
                percentImprovementExpected = calcPercImprovementFromGoalValue(
                    goalValue,
                    calcValues.actual,
                    calcValues.rankOrder,
                );
            }

            if (
                calcValues.goalValue != null &&
                goalValue == null &&
                (!updateFromCellChange || hasActiveCmtKpi != null)
            ) {
                goalValue = calcValues.goalValue;

                percentImprovementExpected = calcPercImprovementFromGoalValue(
                    goalValue,
                    calcValues.actual,
                    calcValues.rankOrder,
                );
            }

            const startPercentToGoal = calcStartPercToGoal(
                goalValue,
                calcValues.actual,
                calcValues.rankOrder,
            );

            const daysToAchievement = differenceInDays(
                startOfDay(expectedAchievementDate),
                startOfDay(new Date()),
            );

            selectedGoals[metricId] = {
                form: {
                    currentActual: currentActual.toFixed(2),
                    expectedAchievementDate,
                    percentGapToTarget: percentGapToTarget.toFixed(2),
                    goalValue: goalValue.toFixed(2),
                    percentImprovementExpected:
                        percentImprovementExpected.toFixed(2),
                    startPercentToGoal: startPercentToGoal.toFixed(2),
                },
                daysToAchievement,
                activeGoal: null,
            };
        }
    }

    return selectedGoals;
}

export function updateGoalsCollection(
    selectedGoals: LoadGoalsSelectedGoals,
    goalsCollection: GoalsCollection,
    goals: FocusSelectorGoalModel[],
    coachableMetrics: FocusSelectorMetricModel[],
): GoalsCollection {
    const updatedGoalsCollection = { ...goalsCollection };
    const commitmentKpis = updatedGoalsCollection.commitmentKpis ?? [];

    Object.keys(selectedGoals).forEach((keyAndMetricId) => {
        const metricId = parseInt(keyAndMetricId);

        // commitment Kpi with a DRAFT status
        let matchingCommitmentKpiIndex = commitmentKpis.findIndex(
            (commitmentKpi) =>
                commitmentKpi.metricId === metricId &&
                !(commitmentKpi.isOriginalGoal ?? false),
        );

        const isDraftKpiFound = matchingCommitmentKpiIndex !== -1;

        // If a DRAFT KPI is not found, then find any commitment KPI for the given metricId
        if (!isDraftKpiFound) {
            matchingCommitmentKpiIndex = commitmentKpis.findIndex(
                (commitmentKpi) => commitmentKpi.metricId === metricId,
            );
        }

        // Get the matching commitment KPI if found;
        const matchingCommitmentKpi =
            matchingCommitmentKpiIndex !== -1
                ? commitmentKpis[matchingCommitmentKpiIndex]
                : null;
        const matchingGoal = goals.find((goal) => goal.metricId === metricId);
        const matchingMetric = coachableMetrics.find(
            (metric) => metric.id === metricId,
        );

        const newCommitmentKpi = focusSelectorSelectedGoaltoCommitmentKPI(
            selectedGoals[keyAndMetricId],
            matchingGoal,
            matchingMetric,
            matchingCommitmentKpi,
        );

        if (matchingCommitmentKpiIndex !== -1 && isDraftKpiFound) {
            // If a DRAFT KPI was found and we have its index, replace it
            commitmentKpis.splice(
                matchingCommitmentKpiIndex,
                1,
                newCommitmentKpi,
            );
        } else {
            // If no DRAFT KPI was found or if we're not replacing, add the new KPI
            commitmentKpis.push(newCommitmentKpi);
        }
    });

    const finalCommitmentKpis = commitmentKpis.filter(
        (commitmentKpi) =>
            (commitmentKpi.statusId === GoalStatus.DRAFT &&
                selectedGoals[commitmentKpi.metricId] != null) ||
            commitmentKpi.statusId !== GoalStatus.DRAFT,
    );

    return { ...updatedGoalsCollection, commitmentKpis: finalCommitmentKpis };
}

export function focusSelectorSelectedGoaltoCommitmentKPI(
    selectedGoal: LoadGoalsSelectedGoal,
    goal: FocusSelectorGoalModel,
    metric: FocusSelectorMetricModel,
    existingCommitmentKpi?: CommitmentKpi,
): CommitmentKpi {
    if (selectedGoal.activeGoal != null) {
        return {
            ...selectedGoal.activeGoal,
            metricId: selectedGoal?.activeGoal?.metricId ?? metric.id,
            metricName: selectedGoal?.activeGoal?.metricName ?? metric.name,
            tracking_id: selectedGoal?.activeGoal?.tracking_id ?? Math.random(),
            shared: true,
            associateToParentFocus: true,
            isOriginalGoal: undefined,
            diffItemId: undefined,
            commitmentId: undefined,
            statusId: GoalStatus.DRAFT,
        };
    }

    return {
        ...existingCommitmentKpi,
        metricId: metric.id,
        metricName: metric.name,
        goalValue: parseFloat(selectedGoal.form.goalValue),
        estimatedCompletionDate: new Date(
            selectedGoal.form.expectedAchievementDate.setHours(0, 0, 0),
        ),
        tracking_id: existingCommitmentKpi?.tracking_id ?? Math.random(),
        actual: goal.actual,
        target: goal.target,
        daysToGoalExpiry: selectedGoal.daysToAchievement,
        updatedOn: new Date(),
        statusId: existingCommitmentKpi?.statusId ?? GoalStatus.DRAFT,
        isOriginalGoal: undefined,
        diffItemId: undefined,
    };
}

export function focusForPerformerFocusHistoryToCommitmentKPI(
    model: FocusForPerformerFocusHistory,
): CommitmentKpi {
    return {
        goalId: model.goalId,
        metricId: model.metricId,
        goalValue: model.goalValue,
        estimatedCompletionDate: model.estimatedCompletionDate,
        metricName: model.metricFullName,
        tracking_id: Math.random(),
        actual: model.actual,
        target: model.target,
        percentToTarget: model.percentToTarget,
        daysToGoalExpiry: model.daysToExpiry,
        statusId: model.goalStatusId,
        isOriginalGoal: undefined,
    };
}

export function setupLoadGoalsState(
    activeMetric: FocusSelectorMetricModel,
    selectedFocus: FocusSelectorFocusModel,
    coachableMetrics: FocusSelectorMetricModel[],
    behaviours: FocusSelectorBehaviourModel[],
    initSelectedFocusMetricIds: number[],
    goalsCollection: GoalsCollection,
    existingLoadGoalsGrid: LoadGoalsGrid,
    commitmentId?: number,
): {
    goals: FocusSelectorState['goals'];
    selectedGoals: FocusSelectorState['selectedGoals'];
    loadGoalsGrid: FocusSelectorState['loadGoalsGrid'];
    updatedGoalsCollection: GoalsCollection;
} {
    let goals: FocusSelectorGoalModel[] = [];
    let selectedGoals: LoadGoalsSelectedGoals = {};
    let loadGoalsGrid: LoadGoalsGrid = {
        ...existingLoadGoalsGrid,
        selection: {
            goals: [],
            projectionEvalCol: {
                metricId: null,
                range: ProjectionEvaluationRangeId.DAY_30,
            },
        },
    };
    let updatedGoalsCollection: GoalsCollection = {
        ...goalsCollection,
        commitmentKpis: [],
    };

    if (
        selectedFocus?.behaviour?.id != null &&
        selectedFocus?.subBehaviour?.id != null &&
        activeMetric?.id != null
    ) {
        const activeCommitmentKpis =
            goalsCollection?.commitmentKpis?.filter(
                (commitmentKpi) =>
                    commitmentKpi.statusId !== GoalStatus.DELETED &&
                    commitmentKpi.statusId !== GoalStatus.CLOSED &&
                    commitmentKpi.statusId !== GoalStatus.PENDING_CLOSE,
            ) ?? [];

        const selectedMetricIdsSet = new Set(
            activeCommitmentKpis.map((commitmentKpi) => commitmentKpi.metricId),
        );

        const selectedMetricIds = Array.from(selectedMetricIdsSet);

        if (initSelectedFocusMetricIds?.length > 0) {
            initSelectedFocusMetricIds.forEach((metricId) => {
                if (!selectedMetricIds.includes(metricId)) {
                    selectedMetricIds.push(metricId);
                }
            });
        }

        if (selectedMetricIds.length === 0) {
            selectedMetricIds.push(activeMetric.id);
        }

        goals = formatGoals(
            selectedMetricIds,
            coachableMetrics,
            selectedFocus,
            behaviours,
        );

        const projectionEvaluationRange =
            loadGoalsGrid?.selection?.projectionEvalCol?.range ??
            ProjectionEvaluationRangeId.DAY_30;

        const isGoalInCurrentCommitment = !(
            goalsCollection?.commitmentKpis?.find(
                (e) => e.metricId === activeMetric?.id,
            ) == null
        );

        selectedGoals = createSelectedGoals(
            selectedMetricIds,
            goals,
            goalsCollection,
            projectionEvaluationRange,
            commitmentId,
            false,
        );

        updatedGoalsCollection = isGoalInCurrentCommitment
            ? goalsCollection
            : updateGoalsCollection(
                  selectedGoals,
                  goalsCollection,
                  goals,
                  coachableMetrics,
              );

        const updatedLoadGoalsRows = makeRows(goals ?? []);

        const loadGoalsSelection: LoadGoalsGrid['selection'] = {
            goals: [],
            projectionEvalCol: {
                metricId: selectedMetricIds[0],
                range: projectionEvaluationRange,
            },
        };

        selectedMetricIds.forEach((metricId) => {
            const foundGoal = findGoal(metricId, goals);
            if (foundGoal != null) {
                loadGoalsSelection.goals.push(foundGoal);
            }
        });

        loadGoalsGrid = {
            ...loadGoalsGrid,
            rows: updatedLoadGoalsRows,
            sortDefs: [...defaultSortDefs],
            selection: loadGoalsSelection,
        };
    }

    return { goals, selectedGoals, loadGoalsGrid, updatedGoalsCollection };
}
